import React, { useState, useEffect, useRef } from 'react';
import Login from './Login';

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.06 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
    }}>{children}</div>
  );
}

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

/* ══ SVG MOCKUPS ══ */
function StockMockup() {
  return (
    <svg viewBox="0 0 580 370" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', borderRadius: '16px', filter: 'drop-shadow(0 32px 64px rgba(30,64,175,0.25))' }}>
      <rect width="580" height="370" rx="14" fill="#f8fafc"/>
      <rect width="580" height="38" rx="14" fill="#1e293b"/>
      <circle cx="18" cy="19" r="5.5" fill="#ef4444"/><circle cx="34" cy="19" r="5.5" fill="#f59e0b"/><circle cx="50" cy="19" r="5.5" fill="#22c55e"/>
      <rect x="68" y="11" width="220" height="16" rx="4" fill="#334155"/>
      <text x="178" y="23" fill="#94a3b8" fontSize="9" textAnchor="middle" fontFamily="monospace">localhost:3000/stock</text>
      <rect x="0" y="38" width="116" height="332" fill="#fff" stroke="#f1f5f9" strokeWidth="1"/>
      <rect x="10" y="50" width="24" height="24" rx="7" fill="#3b82f6"/>
      <text x="22" y="66" fill="#fff" fontSize="13" textAnchor="middle">📦</text>
      <text x="42" y="60" fill="#1e293b" fontSize="9.5" fontWeight="700" fontFamily="sans-serif">StockSpace</text>
      <text x="42" y="72" fill="#94a3b8" fontSize="8" fontFamily="sans-serif">Kavya Singla</text>
      {[{l:'Stock',y:96,a:true},{l:'Orders',y:116},{l:'Log',y:136},{l:'Analytics',y:156},{l:'Users',y:176},{l:'Settings',y:196}].map(({l,y,a})=>(
        <g key={l}>{a&&<rect x="4" y={y-11} width="108" height="22" rx="6" fill="#eff6ff"/>}{a&&<rect x="4" y={y-11} width="3" height="22" rx="2" fill="#3b82f6"/>}
        <text x="20" y={y+4} fill={a?'#3b82f6':'#64748b'} fontSize="9.5" fontFamily="sans-serif" fontWeight={a?'700':'400'}>{l}</text></g>
      ))}
      <rect x="116" y="38" width="464" height="332" fill="#f8fafc"/>
      <text x="132" y="66" fill="#0f172a" fontSize="15" fontWeight="800" fontFamily="sans-serif">Stock Update</text>
      <text x="132" y="80" fill="#94a3b8" fontSize="8.5" fontFamily="sans-serif">3 products · </text>
      <text x="175" y="80" fill="#ef4444" fontSize="8.5" fontFamily="sans-serif">1 below min stock</text>
      <rect x="396" y="52" width="56" height="22" rx="6" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5"/>
      <text x="424" y="66" fill="#374151" fontSize="8.5" textAnchor="middle" fontFamily="sans-serif">↑ Export</text>
      <rect x="458" y="52" width="106" height="22" rx="6" fill="#3b82f6"/>
      <text x="511" y="66" fill="#fff" fontSize="8.5" textAnchor="middle" fontFamily="sans-serif">+ Add Product</text>
      <text x="132" y="102" fill="#374151" fontSize="8.5" fontFamily="sans-serif" fontWeight="700">Transaction type:</text>
      {[{t:'Restock',x:132,a:false},{t:'Sale',x:192,a:true},{t:'Write-off',x:230,a:false},{t:'Return',x:290,a:false},{t:'Adjustment',x:336,a:false}].map(({t,x,a})=>(
        <g key={t}><rect x={x} y="107" width={t.length*5.8+16} height="18" rx="9" fill={a?'#3b82f6':'#fff'} stroke={a?'#3b82f6':'#e2e8f0'} strokeWidth="1.5"/>
        <text x={x+(t.length*5.8+16)/2} y="119" fill={a?'#fff':'#374151'} fontSize="8" textAnchor="middle" fontFamily="sans-serif" fontWeight={a?'700':'400'}>{t}</text></g>
      ))}
      <rect x="132" y="132" width="428" height="24" rx="6" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="1"/>
      <text x="142" y="147" fill="#3b82f6" fontSize="8" fontFamily="sans-serif" fontWeight="700">Sale</text>
      <text x="160" y="147" fill="#3b82f6" fontSize="8" fontFamily="sans-serif">— Sold to customer. Enter quantity and press Enter to decrease stock.</text>
      <rect x="132" y="162" width="130" height="18" rx="5" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="141" y="174" fill="#94a3b8" fontSize="8" fontFamily="sans-serif">Search products...</text>
      {['All','Stationary','PWGroup'].map((t,i)=>(
        <g key={t}><rect x={268+i*(t.length*5.8+18)} y="162" width={t.length*5.8+16} height="18" rx="9" fill={i===0?'#3b82f6':'#fff'} stroke={i===0?'#3b82f6':'#e2e8f0'} strokeWidth="1"/>
        <text x={268+i*(t.length*5.8+18)+(t.length*5.8+16)/2} y="174" fill={i===0?'#fff':'#374151'} fontSize="8" textAnchor="middle" fontFamily="sans-serif">{t}</text></g>
      ))}
      <rect x="132" y="186" width="428" height="20" rx="0" fill="#fff"/>
      {[['PRODUCT NAME',140],['GROUP',280],['STOCK',352],['MIN STOCK',408],['UPDATE',488]].map(([h,x])=>(
        <text key={h} x={x} y="199" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif" fontWeight="700" letterSpacing="0.05em">{h}</text>
      ))}
      {[{n:'Eraser',g:'Stationary',s:'15',sc:'#22c55e',m:'5',h:false},{n:'Erasers',g:'stationary',s:'15',sc:'#22c55e',m:'not set',h:false},{n:'PW Test Item',g:'PWGroup',s:'2',sc:'#ef4444',m:'5',h:true}].map(({n,g,s,sc,m,h},i)=>(
        <g key={n+i}>
          <rect x="132" y={206+i*40} width="428" height="40" fill={h?'#fef2f2':i%2===0?'#fff':'#f8fafc'}/>
          {h&&<rect x="132" y={206+i*40} width="4" height="40" fill="#ef4444"/>}
          <text x="142" y={231+i*40} fill="#0f172a" fontSize="9.5" fontFamily="sans-serif">{n}</text>
          <rect x="278" y={214+i*40} width={g.length*5.4+12} height="16" rx="8" fill={h?'#fce7f3':'#eff6ff'}/>
          <text x={284+(g.length*5.4+12)/2} y={225+i*40} fill={h?'#db2777':'#3b82f6'} fontSize="8" textAnchor="middle" fontFamily="sans-serif">{g}</text>
          <text x="358" y={231+i*40} fill={sc} fontSize="11" fontWeight="800" fontFamily="sans-serif">{s}</text>
          {h&&<><rect x="374" y={216+i*40} width="26" height="14" rx="7" fill="#fef2f2" stroke="#fca5a5" strokeWidth="1"/>
          <text x="387" y={226+i*40} fill="#ef4444" fontSize="7.5" textAnchor="middle" fontFamily="sans-serif">Low</text></>}
          <text x="414" y={231+i*40} fill="#94a3b8" fontSize="8.5" fontFamily="sans-serif">{m}</text>
          <rect x="484" y={214+i*40} width="38" height="18" rx="5" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1"/>
          <text x="503" y={226+i*40} fill="#94a3b8" fontSize="8" textAnchor="middle" fontFamily="sans-serif">qty</text>
        </g>
      ))}
    </svg>
  );
}

/* Updated to match live Inventory & Stock analytics tab: 4 stat cards (Products, Units Sold,
   Write-offs, Slow Movers), sub-tabs (Sales Velocity / Slow Movers / Overstocked), bar chart,
   and ranked product table with Avg/Day + Write-offs columns. */
function AnalyticsStockMockup() {
  const bars = [368, 62, 58, 23, 5];
  const labels = ['jy', 'PW Test Item', 'PW Test Item', 'aks', 'Rubber'];
  const mxV = 380, cH = 86, cY = 196, chartX = 150, chartW = 396;
  const barW = 58, gap = (chartW - bars.length * barW) / (bars.length + 1);
  return (
    <svg viewBox="0 0 580 380" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', borderRadius: '16px', filter: 'drop-shadow(0 32px 64px rgba(30,64,175,0.25))' }}>
      <rect width="580" height="380" rx="14" fill="#f8fafc"/>
      <rect width="580" height="38" rx="14" fill="#1e293b"/>
      <circle cx="18" cy="19" r="5.5" fill="#ef4444"/><circle cx="34" cy="19" r="5.5" fill="#f59e0b"/><circle cx="50" cy="19" r="5.5" fill="#22c55e"/>
      <rect x="68" y="11" width="240" height="16" rx="4" fill="#334155"/>
      <text x="188" y="23" fill="#94a3b8" fontSize="9" textAnchor="middle" fontFamily="monospace">localhost:3000/analytics</text>
      <rect x="0" y="38" width="116" height="342" fill="#fff" stroke="#f1f5f9" strokeWidth="1"/>
      <rect x="10" y="50" width="24" height="24" rx="7" fill="#3b82f6"/>
      <text x="22" y="66" fill="#fff" fontSize="13" textAnchor="middle">📦</text>
      <text x="42" y="60" fill="#1e293b" fontSize="9.5" fontWeight="700" fontFamily="sans-serif">StockSpace</text>
      {[{l:'Stock',y:96},{l:'Orders',y:116},{l:'Log',y:136},{l:'Analytics',y:156,a:true},{l:'Users',y:176},{l:'Settings',y:196}].map(({l,y,a})=>(
        <g key={l}>{a&&<rect x="4" y={y-11} width="108" height="22" rx="6" fill="#eff6ff"/>}{a&&<rect x="4" y={y-11} width="3" height="22" rx="2" fill="#3b82f6"/>}
        <text x="20" y={y+4} fill={a?'#3b82f6':'#64748b'} fontSize="9.5" fontFamily="sans-serif" fontWeight={a?'700':'400'}>{l}</text></g>
      ))}
      <rect x="116" y="38" width="464" height="342" fill="#f8fafc"/>
      <text x="132" y="62" fill="#0f172a" fontSize="15" fontWeight="800" fontFamily="sans-serif">Analytics</text>

      {/* Page-level tabs */}
      <text x="132" y="80" fill="#2563eb" fontSize="8.5" fontFamily="sans-serif" fontWeight="700">📦 Inventory &amp; Stock</text>
      <line x1="132" y1="84" x2="222" y2="84" stroke="#3b82f6" strokeWidth="2"/>
      <text x="232" y="80" fill="#94a3b8" fontSize="8.5" fontFamily="sans-serif">💰 Value &amp; Sales</text>
      <line x1="132" y1="88" x2="560" y2="88" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="132" y="102" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif">Stock insights for the last 30 days</text>
      <rect x="478" y="92" width="82" height="18" rx="5" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="519" y="104" fill="#374151" fontSize="7" textAnchor="middle" fontFamily="sans-serif">Last 30 days ▾</text>

      {/* Stat cards: Products, Units Sold, Write-offs, Slow Movers */}
      {[{l:'Products',v:'6',c:'#3b82f6',bg:'#eff6ff',x:132},{l:'Units Sold',v:'516',c:'#16a34a',bg:'#f0fdf4',x:242},{l:'Write-offs',v:'0',c:'#f97316',bg:'#fff7ed',x:352},{l:'Slow Movers',v:'3',c:'#ef4444',bg:'#fef2f2',x:462}].map(({l,v,c,bg,x})=>(
        <g key={l}><rect x={x} y="114" width="100" height="46" rx="8" fill={bg}/>
        <text x={x+12} y="138" fill={c} fontSize="17" fontWeight="800" fontFamily="sans-serif">{v}</text>
        <text x={x+12} y="152" fill="#94a3b8" fontSize="7" fontFamily="sans-serif">{l}</text></g>
      ))}

      {/* Sub-tabs */}
      {[{t:'📈 Sales Velocity',x:132,a:true},{t:'🐢 Slow Movers (3)',x:226,a:false},{t:'📦 Overstocked (4)',x:330,a:false}].map(({t,x,a})=>(
        <g key={t}><rect x={x} y="170" width={t.length*5.3+14} height="17" rx="5" fill={a?'#fff':'transparent'} stroke={a?'#e2e8f0':'transparent'} strokeWidth="1"/>
        <text x={x+7} y="181" fill={a?'#0f172a':'#64748b'} fontSize="7.5" fontFamily="sans-serif" fontWeight={a?'700':'400'}>{t}</text></g>
      ))}

      <text x="132" y="202" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif">Units sold per product over the last 30 days</text>
      <rect x="478" y="192" width="70" height="18" rx="5" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5"/>
      <text x="513" y="204" fill="#374151" fontSize="7" textAnchor="middle" fontFamily="sans-serif">⬇ Export</text>

      {/* Bar chart card */}
      <rect x="132" y="210" width="428" height="116" rx="8" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      {[0,95,190,285,380].map(v=>(
        <text key={v} x="146" y={cY+cH-(v/mxV)*cH+3} fill="#cbd5e1" fontSize="6.5" textAnchor="end" fontFamily="monospace">{v}</text>
      ))}
      {bars.map((v,i)=>{
        const bh=(v/mxV)*cH, bx=chartX+gap*(i+1)+barW*i+10, by=cY+cH-bh;
        const shades=['#3b82f6','#60a5fa','#93c5fd','#bfdbfe','#dbeafe'];
        return <rect key={i} x={bx} y={by} width={barW} height={Math.max(bh,2)} rx="3" fill={shades[i]}/>;
      })}
      {labels.map((l,i)=>{
        const bx=chartX+gap*(i+1)+barW*i+10+barW/2;
        return <text key={i} x={bx} y={cY+cH+16} fill="#94a3b8" fontSize="7" textAnchor="middle" fontFamily="sans-serif" transform={`rotate(-18 ${bx} ${cY+cH+16})`}>{l}</text>;
      })}

      {/* Ranked table */}
      <rect x="132" y="332" width="428" height="20" fill="#fff"/>
      {[['#',138],['PRODUCT',152],['GROUP',270],['SOLD (30D)',356],['AVG/DAY',430],['WRITE-OFFS',494]].map(([h,x])=>(
        <text key={h} x={x} y="344" fill="#94a3b8" fontSize="7" fontFamily="sans-serif" fontWeight="700" letterSpacing="0.04em">{h}</text>
      ))}
      <rect x="132" y="352" width="428" height="22" fill="#fff"/>
      <text x="138" y="367" fill="#64748b" fontSize="8" fontFamily="sans-serif">1</text>
      <text x="152" y="367" fill="#0f172a" fontSize="8.5" fontFamily="sans-serif" fontWeight="600">jy</text>
      <rect x="270" y="358" width="28" height="14" rx="7" fill="#eff6ff"/>
      <text x="284" y="368" fill="#3b82f6" fontSize="7" textAnchor="middle" fontFamily="sans-serif">ugyj</text>
      <rect x="356" y="358" width="30" height="14" rx="5" fill="#eff6ff"/>
      <text x="371" y="368" fill="#1d4ed8" fontSize="7.5" textAnchor="middle" fontFamily="sans-serif" fontWeight="700">368</text>
      <text x="430" y="367" fill="#64748b" fontSize="7.5" fontFamily="sans-serif">12.27/day</text>
      <text x="494" y="367" fill="#cbd5e1" fontSize="8" fontFamily="sans-serif">—</text>
    </svg>
  );
}

/* Updated to match live Value & Sales analytics tab: Revenue, Units Sold, Dead Stock SKUs,
   Products Sold stat cards; empty Revenue by Group card; Best Sellers/Revenue/Least Sold/Dead
   Stock sub-tabs; bar chart matching real product names and proportions. */
function AnalyticsValueMockup() {
  const bars = [368, 62, 58, 8, 5];
  const mxV = 380, cH = 86, cY = 318, chartX = 150, chartW = 396;
  const barW = 58, gap = (chartW - bars.length * barW) / (bars.length + 1);
  return (
    <svg viewBox="0 0 580 460" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', borderRadius: '16px', filter: 'drop-shadow(0 32px 64px rgba(30,64,175,0.25))' }}>
      <rect width="580" height="460" rx="14" fill="#f8fafc"/>
      <rect width="580" height="38" rx="14" fill="#1e293b"/>
      <circle cx="18" cy="19" r="5.5" fill="#ef4444"/><circle cx="34" cy="19" r="5.5" fill="#f59e0b"/><circle cx="50" cy="19" r="5.5" fill="#22c55e"/>
      <rect x="68" y="11" width="240" height="16" rx="4" fill="#334155"/>
      <text x="188" y="23" fill="#94a3b8" fontSize="9" textAnchor="middle" fontFamily="monospace">localhost:3000/analytics</text>
      <rect x="0" y="38" width="116" height="422" fill="#fff" stroke="#f1f5f9" strokeWidth="1"/>
      <rect x="10" y="50" width="24" height="24" rx="7" fill="#3b82f6"/>
      <text x="22" y="66" fill="#fff" fontSize="13" textAnchor="middle">📦</text>
      <text x="42" y="60" fill="#1e293b" fontSize="9.5" fontWeight="700" fontFamily="sans-serif">StockSpace</text>
      {[{l:'Stock',y:96},{l:'Orders',y:116},{l:'Log',y:136},{l:'Analytics',y:156,a:true},{l:'Users',y:176},{l:'Settings',y:196}].map(({l,y,a})=>(
        <g key={l}>{a&&<rect x="4" y={y-11} width="108" height="22" rx="6" fill="#eff6ff"/>}{a&&<rect x="4" y={y-11} width="3" height="22" rx="2" fill="#3b82f6"/>}
        <text x="20" y={y+4} fill={a?'#3b82f6':'#64748b'} fontSize="9.5" fontFamily="sans-serif" fontWeight={a?'700':'400'}>{l}</text></g>
      ))}
      <rect x="116" y="38" width="464" height="422" fill="#f8fafc"/>
      <text x="132" y="62" fill="#0f172a" fontSize="15" fontWeight="800" fontFamily="sans-serif">Analytics</text>

      {/* Page-level tabs */}
      <text x="132" y="80" fill="#94a3b8" fontSize="8.5" fontFamily="sans-serif">📦 Inventory &amp; Stock</text>
      <text x="222" y="80" fill="#2563eb" fontSize="8.5" fontFamily="sans-serif" fontWeight="700">💰 Value &amp; Sales</text>
      <line x1="222" y1="84" x2="290" y2="84" stroke="#3b82f6" strokeWidth="2"/>
      <line x1="132" y1="88" x2="560" y2="88" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="132" y="102" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif">Sales &amp; revenue for the last 30 days</text>
      <rect x="478" y="92" width="82" height="18" rx="5" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="519" y="104" fill="#374151" fontSize="7" textAnchor="middle" fontFamily="sans-serif">Last 30 days ▾</text>

      {/* Stat cards: Total Revenue, Units Sold, Dead Stock SKUs, Products Sold */}
      {[{l:'Total Revenue',v:'—',c:'#16a34a',bg:'#f0fdf4',x:132},{l:'Units Sold',v:'516',c:'#3b82f6',bg:'#eff6ff',x:242},{l:'Dead Stock SKUs',v:'3',c:'#9333ea',bg:'#fdf4ff',x:352},{l:'Products Sold',v:'5',c:'#f97316',bg:'#fff7ed',x:462}].map(({l,v,c,bg,x})=>(
        <g key={l}><rect x={x} y="114" width="100" height="46" rx="8" fill={bg}/>
        <text x={x+12} y="138" fill={c} fontSize="17" fontWeight="800" fontFamily="sans-serif">{v}</text>
        <text x={x+12} y="152" fill="#94a3b8" fontSize="7" fontFamily="sans-serif">{l}</text></g>
      ))}

      {/* Revenue by Group card (empty state — needs cost prices) */}
      <rect x="132" y="168" width="428" height="118" rx="8" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="148" y="186" fill="#0f172a" fontSize="9.5" fontWeight="700" fontFamily="sans-serif">Revenue by Group</text>
      <text x="148" y="199" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif">Share of total revenue per product group</text>
      <text x="346" y="232" fill="#cbd5e1" fontSize="8" textAnchor="middle" fontFamily="sans-serif" fontStyle="italic">Add cost &amp; sale prices to see revenue breakdown</text>

      {/* Sub-tabs: Best Sellers | Revenue | Least Sold | Dead Stock */}
      {[{t:'🏆 Best Sellers',x:132,a:true},{t:'💰 Revenue',x:226,a:false},{t:'📉 Least Sold',x:294,a:false},{t:'💀 Dead Stock (3)',x:366,a:false}].map(({t,x,a})=>(
        <g key={t}><rect x={x} y="294" width={t.length*4.9+14} height="17" rx="5" fill={a?'#fff':'transparent'} stroke={a?'#e2e8f0':'transparent'} strokeWidth="1"/>
        <text x={x+7} y="305" fill={a?'#0f172a':'#64748b'} fontSize="7.5" fontFamily="sans-serif" fontWeight={a?'700':'400'}>{t}</text></g>
      ))}

      <text x="132" y="326" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif">Products ranked by units sold — 30d</text>
      <rect x="412" y="314" width="70" height="18" rx="5" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5"/>
      <text x="447" y="326" fill="#374151" fontSize="7" textAnchor="middle" fontFamily="sans-serif">⬇ Export Excel</text>
      <rect x="488" y="314" width="44" height="18" rx="5" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5"/>
      <text x="510" y="326" fill="#374151" fontSize="7" textAnchor="middle" fontFamily="sans-serif">🖨 Print</text>

      {/* Bar chart card */}
      <rect x="132" y="332" width="428" height="112" rx="8" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      {bars.map((v,i)=>{
        const bh=(v/mxV)*cH, bx=chartX+gap*(i+1)+barW*i+10, by=cY+cH-bh;
        const shades=['#3b82f6','#60a5fa','#93c5fd','#bfdbfe','#dbeafe'];
        return <rect key={i} x={bx} y={by} width={barW} height={Math.max(bh,2)} rx="3" fill={shades[i]}/>;
      })}
    </svg>
  );
}

/* New — Activity Log with AI Search (Gemini-powered natural language query). */
function ActivityLogMockup() {
  return (
    <svg viewBox="0 0 580 300" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', borderRadius: '16px', filter: 'drop-shadow(0 32px 64px rgba(30,64,175,0.25))' }}>
      <rect width="580" height="300" rx="14" fill="#f8fafc"/>
      <rect width="580" height="38" rx="14" fill="#1e293b"/>
      <circle cx="18" cy="19" r="5.5" fill="#ef4444"/><circle cx="34" cy="19" r="5.5" fill="#f59e0b"/><circle cx="50" cy="19" r="5.5" fill="#22c55e"/>
      <rect x="68" y="11" width="200" height="16" rx="4" fill="#334155"/>
      <text x="168" y="23" fill="#94a3b8" fontSize="9" textAnchor="middle" fontFamily="monospace">localhost:3000/log</text>
      <rect x="0" y="38" width="116" height="262" fill="#fff" stroke="#f1f5f9" strokeWidth="1"/>
      <rect x="10" y="50" width="24" height="24" rx="7" fill="#3b82f6"/>
      <text x="22" y="66" fill="#fff" fontSize="13" textAnchor="middle">📦</text>
      <text x="42" y="60" fill="#1e293b" fontSize="9.5" fontWeight="700" fontFamily="sans-serif">StockSpace</text>
      {[{l:'Stock',y:96},{l:'Orders',y:116},{l:'Log',y:136,a:true},{l:'Analytics',y:156},{l:'Users',y:176},{l:'Settings',y:196}].map(({l,y,a})=>(
        <g key={l}>{a&&<rect x="4" y={y-11} width="108" height="22" rx="6" fill="#eff6ff"/>}{a&&<rect x="4" y={y-11} width="3" height="22" rx="2" fill="#3b82f6"/>}
        <text x="20" y={y+4} fill={a?'#3b82f6':'#64748b'} fontSize="9.5" fontFamily="sans-serif" fontWeight={a?'700':'400'}>{l}</text></g>
      ))}
      <rect x="116" y="38" width="464" height="262" fill="#f8fafc"/>
      <text x="132" y="64" fill="#0f172a" fontSize="15" fontWeight="800" fontFamily="sans-serif">Activity Log</text>
      <text x="132" y="78" fill="#94a3b8" fontSize="8.5" fontFamily="sans-serif">Showing 45 of 45 loaded entries</text>
      <rect x="478" y="50" width="70" height="20" rx="5" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5"/>
      <text x="513" y="63" fill="#374151" fontSize="7.5" textAnchor="middle" fontFamily="sans-serif">⬇ Export</text>

      {/* AI Search card */}
      <rect x="132" y="92" width="428" height="78" rx="9" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5"/>
      <text x="148" y="112" fill="#f59e0b" fontSize="9" fontFamily="sans-serif">✨</text>
      <text x="160" y="112" fill="#0f172a" fontSize="9" fontWeight="700" fontFamily="sans-serif">AI Search</text>
      <text x="206" y="112" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif" fontStyle="italic">powered by Gemini · pre-fills filters below</text>
      <rect x="148" y="122" width="320" height="24" rx="6" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="158" y="137" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif">Ask in plain English — e.g. "who sold the most last week"</text>
      <rect x="476" y="122" width="68" height="24" rx="6" fill="#bfdbfe"/>
      <text x="510" y="137" fill="#fff" fontSize="8" textAnchor="middle" fontFamily="sans-serif" fontWeight="700">Search</text>

      {/* Filters below */}
      <rect x="132" y="178" width="160" height="18" rx="5" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="142" y="190" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif">Search products...</text>
      <text x="132" y="216" fill="#374151" fontSize="8" fontFamily="sans-serif" fontWeight="700">Period:</text>
      {['Last 24h','Last 7d','Last 30d','Last Year','All time'].map((t,i)=>(
        <g key={t}><rect x={132+i*64} y="222" width="58" height="18" rx="9" fill={i===2?'#3b82f6':'#fff'} stroke={i===2?'#3b82f6':'#e2e8f0'} strokeWidth="1.5"/>
        <text x={161+i*64} y="234" fill={i===2?'#fff':'#374151'} fontSize="7" textAnchor="middle" fontFamily="sans-serif" fontWeight={i===2?'700':'400'}>{t}</text></g>
      ))}
      <text x="132" y="256" fill="#374151" fontSize="8" fontFamily="sans-serif" fontWeight="700">Type:</text>
      {['All','Restock','Sale','Write-off','Return','Adjustment'].map((t,i)=>(
        <g key={t}><rect x={132+i*62} y="262" width="56" height="18" rx="9" fill={i===0?'#3b82f6':'#fff'} stroke={i===0?'#3b82f6':'#e2e8f0'} strokeWidth="1.5"/>
        <text x={160+i*62} y="274" fill={i===0?'#fff':'#374151'} fontSize="6.8" textAnchor="middle" fontFamily="sans-serif" fontWeight={i===0?'700':'400'}>{t}</text></g>
      ))}
    </svg>
  );
}

/* Updated dashboard hero — Inventory Value (by group, real ₹1,000 / 1 of 6 priced),
   Sales — Last 7 Days mini chart, and Low Stock Alerts panel, matching the live dashboard. */
function DashboardValueMockup() {
  return (
    <svg viewBox="0 0 580 370" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', borderRadius: '16px', filter: 'drop-shadow(0 32px 64px rgba(30,64,175,0.25))' }}>
      <rect width="580" height="370" rx="14" fill="#f8fafc"/>
      <rect width="580" height="38" rx="14" fill="#1e293b"/>
      <circle cx="18" cy="19" r="5.5" fill="#ef4444"/><circle cx="34" cy="19" r="5.5" fill="#f59e0b"/><circle cx="50" cy="19" r="5.5" fill="#22c55e"/>
      <rect x="68" y="11" width="220" height="16" rx="4" fill="#334155"/>
      <text x="178" y="23" fill="#94a3b8" fontSize="9" textAnchor="middle" fontFamily="monospace">localhost:3000/dashboard</text>
      <rect x="0" y="38" width="116" height="332" fill="#fff" stroke="#f1f5f9" strokeWidth="1"/>
      <rect x="10" y="50" width="24" height="24" rx="7" fill="#3b82f6"/>
      <text x="22" y="66" fill="#fff" fontSize="13" textAnchor="middle">📦</text>
      <text x="42" y="60" fill="#1e293b" fontSize="9.5" fontWeight="700" fontFamily="sans-serif">StockSpace</text>
      <text x="42" y="72" fill="#94a3b8" fontSize="8" fontFamily="sans-serif">Kavya Singla</text>
      {[{l:'Stock',y:96},{l:'Orders',y:116},{l:'Log',y:136},{l:'Analytics',y:156},{l:'Users',y:176},{l:'Settings',y:196}].map(({l,y})=>(
        <text key={l} x="20" y={y+4} fill="#64748b" fontSize="9.5" fontFamily="sans-serif">{l}</text>
      ))}
      <rect x="116" y="38" width="464" height="332" fill="#f8fafc"/>
      <text x="132" y="64" fill="#0f172a" fontSize="15" fontWeight="800" fontFamily="sans-serif">Good morning, Kavya 👋</text>
      <text x="132" y="78" fill="#94a3b8" fontSize="8" fontFamily="sans-serif">Saturday, 20 June 2026</text>

      {/* Inventory Value card */}
      <rect x="132" y="92" width="428" height="100" rx="10" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <rect x="132" y="92" width="4" height="100" rx="2" fill="#8b5cf6"/>
      <text x="148" y="112" fill="#0f172a" fontSize="11.5" fontWeight="700" fontFamily="sans-serif">Inventory Value</text>
      <text x="148" y="132" fill="#8b5cf6" fontSize="18" fontWeight="800" fontFamily="sans-serif">₹1,000</text>
      <text x="216" y="132" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif">· 1 of 6 products priced</text>
      <rect x="412" y="100" width="138" height="20" rx="6" fill="#f1f5f9"/>
      <rect x="412" y="100" width="68" height="20" rx="6" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="446" y="113" fill="#1e293b" fontSize="7.5" fontWeight="700" textAnchor="middle" fontFamily="sans-serif">By group</text>
      <text x="515" y="113" fill="#94a3b8" fontSize="7.5" textAnchor="middle" fontFamily="sans-serif">Top items</text>
      <text x="148" y="150" fill="#374151" fontSize="8.5" fontFamily="sans-serif" fontWeight="600">grains</text>
      <rect x="148" y="156" width="396" height="20" rx="5" fill="#8b5cf6"/>
      <text x="500" y="170" fill="#fff" fontSize="8" fontFamily="sans-serif" fontWeight="700" textAnchor="end">₹1,000</text>
      <text x="538" y="170" fill="#fff" fontSize="7.5" fontFamily="sans-serif" fontWeight="600">100%</text>

      {/* SALES ACTIVITY label */}
      <text x="132" y="212" fill="#94a3b8" fontSize="8" fontFamily="sans-serif" fontWeight="700" letterSpacing="0.05em">📈 SALES ACTIVITY</text>

      {/* Sales — Last 7 Days card */}
      <rect x="132" y="222" width="208" height="124" rx="9" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="146" y="240" fill="#0f172a" fontSize="9.5" fontWeight="700" fontFamily="sans-serif">Sales — Last 7 Days</text>
      <text x="146" y="252" fill="#94a3b8" fontSize="7" fontFamily="sans-serif">Units sold per day</text>
      <text x="306" y="240" fill="#3b82f6" fontSize="7.5" fontFamily="sans-serif" fontWeight="600">Full analytics →</text>
      {[0,70,140,210,280].map(v=>(
        <text key={v} x="148" y={332-(v/280)*78} fill="#cbd5e1" fontSize="6" textAnchor="end" fontFamily="monospace">{v}</text>
      ))}
      {[{d:'Wed',v:62},{d:'Thu',v:0},{d:'Fri',v:0},{d:'Sat',v:0},{d:'Sun',v:0},{d:'Mon',v:266},{d:'Tue',v:0}].map(({d,v},i)=>{
        const bh=(v/280)*78, bx=158+i*25, by=332-bh;
        return <g key={d}>
          {v>0 && <rect x={bx} y={by} width="9" height={bh} rx="2" fill="#93c5fd"/>}
          <text x={bx+4} y="342" fill="#94a3b8" fontSize="6.5" textAnchor="middle" fontFamily="sans-serif">{d}</text>
        </g>;
      })}

      {/* Low Stock Alerts card */}
      <rect x="350" y="222" width="210" height="124" rx="9" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x="364" y="240" fill="#0f172a" fontSize="9.5" fontWeight="700" fontFamily="sans-serif">Low Stock Alerts</text>
      <text x="364" y="252" fill="#94a3b8" fontSize="7" fontFamily="sans-serif">Items needing restock</text>
      <text x="524" y="240" fill="#3b82f6" fontSize="7.5" fontFamily="sans-serif" fontWeight="600">Manage →</text>
      <circle cx="368" cy="270" r="3" fill="#ef4444"/>
      <text x="378" y="273" fill="#0f172a" fontSize="9" fontWeight="700" fontFamily="sans-serif">Notebooks</text>
      <text x="378" y="284" fill="#94a3b8" fontSize="7" fontFamily="sans-serif">ugyj</text>
      <text x="552" y="273" fill="#ef4444" fontSize="11" fontWeight="800" fontFamily="sans-serif" textAnchor="end">62</text>
      <text x="552" y="284" fill="#94a3b8" fontSize="6.5" fontFamily="sans-serif" textAnchor="end">need 58 more</text>
    </svg>
  );
}

/* New — AI Demand Forecast card (Gemini-powered stockout predictions). */
function AIForecastMockup() {
  const items = [
    { name: 'Notebooks', stock: 62, avg: '33.4 units/day', conf: 'medium', confColor: '#d97706', days: '~1 days left', note: 'High recent sales, low stock, and few data points indicate medium confidence.', order: 'Order 1005 units' },
    { name: 'PW Test Item', stock: 8, avg: '4.4 units/day', conf: 'low', confColor: '#64748b', days: '~1 days left', note: 'Highly variable sales, recent spike skews average, low confidence in prediction.', order: 'Order 135 units' },
    { name: 'Registers', stock: 50, avg: null, conf: 'low', confColor: '#64748b', days: null, note: 'Not enough data yet — needs at least 3 sale events in 14 days.', order: 'No data' },
  ];
  return (
    <svg viewBox="0 0 580 340" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', borderRadius: '16px', filter: 'drop-shadow(0 32px 64px rgba(30,64,175,0.25))' }}>
      <rect width="580" height="340" rx="14" fill="#f8fafc"/>
      <rect x="14" y="14" width="552" height="312" rx="12" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5"/>
      <rect x="14" y="14" width="4" height="312" rx="2" fill="#6366f1"/>

      <text x="38" y="38" fill="#0f172a" fontSize="13" fontWeight="700" fontFamily="sans-serif">🤖 Demand Forecast</text>
      <rect x="182" y="26" width="52" height="16" rx="8" fill="#eef2ff"/>
      <text x="208" y="37" fill="#6366f1" fontSize="7.5" textAnchor="middle" fontFamily="sans-serif" fontWeight="700">Gemini</text>
      <text x="38" y="52" fill="#94a3b8" fontSize="8" fontFamily="sans-serif">AI-powered stockout predictions · cached 24h · last run 09:41 AM</text>
      <rect x="486" y="26" width="68" height="20" rx="6" fill="#fff" stroke="#e2e8f0" strokeWidth="1.5"/>
      <text x="520" y="39" fill="#374151" fontSize="7.5" textAnchor="middle" fontFamily="sans-serif">↻ Refresh</text>
      <line x1="38" y1="62" x2="554" y2="62" stroke="#f1f5f9" strokeWidth="1"/>

      {items.map((it, i) => {
        const y = 78 + i * 86;
        return (
          <g key={it.name}>
            <text x="38" y={y} fill="#0f172a" fontSize="11" fontWeight="700" fontFamily="sans-serif">{it.name}</text>
            <rect x="38" y={y+10} width="58" height="16" rx="8" fill="#fef3c7"/>
            <text x="67" y={y+21} fill="#92400e" fontSize="7" textAnchor="middle" fontFamily="sans-serif">📦 Stock: {it.stock}</text>
            {it.avg && <>
              <rect x="102" y={y+10} width={it.avg.length*4.6+30} height="16" rx="8" fill="#eff6ff"/>
              <text x={102+(it.avg.length*4.6+30)/2} y={y+21} fill="#2563eb" fontSize="7" textAnchor="middle" fontFamily="sans-serif">📉 avg {it.avg}</text>
            </>}
            <rect x={it.avg ? 102+it.avg.length*4.6+36 : 102} y={y+10} width={it.conf.length*5+58} height="16" rx="8" fill="#f8fafc"/>
            <circle cx={(it.avg ? 102+it.avg.length*4.6+36 : 102)+10} cy={y+18} r="3" fill={it.confColor}/>
            <text x={(it.avg ? 102+it.avg.length*4.6+36 : 102)+18} y={y+21} fill="#64748b" fontSize="7" fontFamily="sans-serif">{it.conf} confidence</text>
            {it.days && <>
              <rect x="450" y={y-12} width="104" height="20" rx="10" fill="#fef2f2" stroke="#fca5a5" strokeWidth="1"/>
              <text x="502" y={y+1} fill="#dc2626" fontSize="8" textAnchor="middle" fontFamily="sans-serif" fontWeight="700">{it.days}</text>
            </>}
            <text x="38" y={y+38} fill="#94a3b8" fontSize="7.5" fontStyle="italic" fontFamily="sans-serif">{it.note.length > 70 ? it.note.slice(0,70)+'…' : it.note}</text>
            <rect x={it.order==='No data'?470:432} y={y+44} width={it.order==='No data'?84:122} height="18" rx="9" fill={it.order==='No data'?'#f1f5f9':'#eff6ff'} stroke={it.order==='No data'?'#e2e8f0':'#bfdbfe'} strokeWidth="1"/>
            <text x={it.order==='No data'?512:493} y={y+56} fill={it.order==='No data'?'#94a3b8':'#2563eb'} fontSize="7.5" textAnchor="middle" fontFamily="sans-serif" fontWeight="600">{it.order==='No data'?it.order:'🛒 '+it.order}</text>
            {i < items.length-1 && <line x1="38" y1={y+66} x2="554" y2={y+66} stroke="#f1f5f9" strokeWidth="1"/>}
          </g>
        );
      })}
    </svg>
  );
}

function TeamMockup() {
  const users=[{n:'Kavya Singla',e:'kavya@example.com',r:'admin',i:'KS',c:'#3b82f6'},{n:'Rohan Mehta',e:'rohan@example.com',r:'manager',i:'RM',c:'#8b5cf6'},{n:'Sara Ahmed',e:'sara@example.com',r:'staff',i:'SA',c:'#10b981'},{n:'Dev Patel',e:'dev@example.com',r:'staff',i:'DP',c:'#f59e0b'}];
  const rc={admin:'#eff6ff',manager:'#f5f3ff',staff:'#f0fdf4'};
  const rt={admin:'#1d4ed8',manager:'#7c3aed',staff:'#059669'};
  return (
    <svg viewBox="0 0 580 320" xmlns="http://www.w3.org/2000/svg"
      style={{width:'100%',borderRadius:'16px',filter:'drop-shadow(0 32px 64px rgba(30,64,175,0.25))'}}>
      <rect width="580" height="320" rx="14" fill="#f8fafc"/>
      <rect width="580" height="38" rx="14" fill="#1e293b"/>
      <circle cx="18" cy="19" r="5.5" fill="#ef4444"/><circle cx="34" cy="19" r="5.5" fill="#f59e0b"/><circle cx="50" cy="19" r="5.5" fill="#22c55e"/>
      <rect x="68" y="11" width="200" height="16" rx="4" fill="#334155"/>
      <text x="168" y="23" fill="#94a3b8" fontSize="9" textAnchor="middle" fontFamily="monospace">localhost:3000/users</text>
      <rect x="0" y="38" width="116" height="282" fill="#fff" stroke="#f1f5f9" strokeWidth="1"/>
      <rect x="10" y="50" width="24" height="24" rx="7" fill="#3b82f6"/>
      <text x="22" y="66" fill="#fff" fontSize="13" textAnchor="middle">📦</text>
      <text x="42" y="60" fill="#1e293b" fontSize="9.5" fontWeight="700" fontFamily="sans-serif">StockSpace</text>
      {[{l:'Stock',y:96},{l:'Orders',y:116},{l:'Log',y:136},{l:'Analytics',y:156},{l:'Users',y:176,a:true},{l:'Settings',y:196}].map(({l,y,a})=>(
        <g key={l}>{a&&<rect x="4" y={y-11} width="108" height="22" rx="6" fill="#eff6ff"/>}{a&&<rect x="4" y={y-11} width="3" height="22" rx="2" fill="#3b82f6"/>}
        <text x="20" y={y+4} fill={a?'#3b82f6':'#64748b'} fontSize="9.5" fontFamily="sans-serif" fontWeight={a?'700':'400'}>{l}</text></g>
      ))}
      <rect x="116" y="38" width="464" height="282" fill="#f8fafc"/>
      <text x="132" y="66" fill="#0f172a" fontSize="15" fontWeight="800" fontFamily="sans-serif">User Management</text>
      <text x="132" y="80" fill="#94a3b8" fontSize="8.5" fontFamily="sans-serif">4 members in your organisation</text>
      <rect x="460" y="54" width="106" height="22" rx="6" fill="#3b82f6"/>
      <text x="513" y="68" fill="#fff" fontSize="8.5" textAnchor="middle" fontFamily="sans-serif">+ Invite Member</text>
      {[{t:'1 Admin',x:132,c:'#1d4ed8',bg:'#eff6ff'},{t:'1 Manager',x:196,c:'#7c3aed',bg:'#f5f3ff'},{t:'2 Staff',x:270,c:'#059669',bg:'#f0fdf4'}].map(({t,x,c,bg})=>(
        <g key={t}><rect x={x} y="90" width={t.length*5.8+16} height="18" rx="9" fill={bg}/>
        <text x={x+(t.length*5.8+16)/2} y="102" fill={c} fontSize="8" textAnchor="middle" fontFamily="sans-serif" fontWeight="700">{t}</text></g>
      ))}
      <rect x="132" y="116" width="428" height="18" fill="#fff"/>
      {[['USER',140],['EMAIL',270],['ROLE',420],['ACTIONS',500]].map(([h,x])=>(
        <text key={h} x={x} y="128" fill="#94a3b8" fontSize="7.5" fontFamily="sans-serif" fontWeight="700" letterSpacing="0.05em">{h}</text>
      ))}
      {users.map(({n,e,r,i,c},idx)=>(
        <g key={n}>
          <rect x="132" y={134+idx*42} width="428" height="42" fill={idx%2===0?'#fff':'#f8fafc'}/>
          <circle cx="158" cy={155+idx*42} r="13" fill={c+'20'}/>
          <text x="158" y={159+idx*42} fill={c} fontSize="9" fontWeight="700" textAnchor="middle" fontFamily="sans-serif">{i}</text>
          <text x="178" y={157+idx*42} fill="#0f172a" fontSize="9.5" fontFamily="sans-serif" fontWeight="600">{n}</text>
          <text x="270" y={157+idx*42} fill="#64748b" fontSize="8.5" fontFamily="sans-serif">{e}</text>
          <rect x="418" y={145+idx*42} width={r.length*6+14} height="16" rx="8" fill={rc[r]}/>
          <text x={418+(r.length*6+14)/2} y={156+idx*42} fill={rt[r]} fontSize="8" textAnchor="middle" fontFamily="sans-serif" fontWeight="700">{r}</text>
          <rect x="500" y={145+idx*42} width="42" height="16" rx="5" fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="1"/>
          <text x="521" y={156+idx*42} fill="#64748b" fontSize="8" textAnchor="middle" fontFamily="sans-serif">Edit</text>
        </g>
      ))}
    </svg>
  );
}

/* ══ FULL-WIDTH SECTION WRAPPER ══ */
function FullBleed({ id, bg, children, py = '96px' }) {
  return (
    <div id={id} style={{
      width: '100vw', marginLeft: 'calc(-50vw + 50%)',
      background: bg, padding: `${py} 0`,
      scrollMarginTop: '0px',
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 72px' }}>
        {children}
      </div>
    </div>
  );
}

/* ══ FEATURE SECTION ══ */
function Feature({ tag, tagColor = '#3b82f6', tagBg = 'rgba(59,130,246,0.08)', tagBorder = 'rgba(59,130,246,0.2)', title, desc, bullets, bulletColor = '#2563eb', mockup, reverse, isMobile }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : reverse ? 'row-reverse' : 'row',
      alignItems: 'center', gap: isMobile ? '40px' : '72px',
    }}>
      <div style={{ flex: '0 0 420px', minWidth: 0 }}>
        <div style={{
          display: 'inline-block', fontSize: '11px', fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          color: tagColor, background: tagBg,
          border: `1px solid ${tagBorder}`,
          borderRadius: '6px', padding: '4px 12px', marginBottom: '18px',
        }}>{tag}</div>
        <h2 style={{
          fontFamily: "'Syne',sans-serif",
          fontSize: isMobile ? '28px' : '38px',
          fontWeight: 800, color: '#0f172a',
          lineHeight: 1.12, letterSpacing: '-0.025em', marginBottom: '16px',
        }}>{title}</h2>
        <p style={{ fontSize: '16px', color: '#475569', lineHeight: 1.75, marginBottom: '28px' }}>{desc}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
          {bullets.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{
                width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
                background: `linear-gradient(135deg, ${bulletColor}, ${bulletColor}cc)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="10" height="10" viewBox="0 0 10 10">
                  <polyline points="2,5 4,7 8,3" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span style={{ fontSize: '15px', color: '#334155', lineHeight: 1.6 }}>{b}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0, width: '100%' }}>{mockup}</div>
    </div>
  );
}

/* ══ STATS BAR ══ (added AI stat) */
function StatsBar({ isMobile }) {
  const items = [
    { icon: '📦', val: '100%', label: 'Free forever' },
    { icon: '⚡', val: '< 1s', label: 'Real-time sync' },
    { icon: '🔄', val: '5+', label: 'Transaction types' },
    { icon: '🤖', val: 'Gemini', label: 'AI forecasting & search' },
    { icon: '🔒', val: 'Role-based', label: 'Access control' },
  ];
  return (
    <div style={{
      width: '100vw', marginLeft: 'calc(-50vw + 50%)',
      background: 'linear-gradient(90deg, #1e3a8a 0%, #1d4ed8 50%, #2563eb 100%)',
      padding: '28px 0',
      display: 'flex', justifyContent: 'center',
      overflow: 'hidden',
    }}>
      <div style={{
        maxWidth: '1280px', width: '100%', padding: '0 72px',
        display: 'flex',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        justifyContent: 'space-between', gap: '20px',
      }}>
        {items.map(({ icon, val, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: isMobile ? '1 0 40%' : 1 }}>
            <span style={{ fontSize: '22px' }}>{icon}</span>
            <div>
              <div style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '17px', fontWeight: 700, color: '#fff', letterSpacing: '-0.02em',
              }}>{val}</div>
              <div style={{ fontSize: '11px', color: '#93c5fd', fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══ HOW IT WORKS ══ */
function HowItWorks({ isMobile }) {
  const steps = [
    { n: '01', icon: '🏢', title: 'Create your organisation', desc: 'Sign up and set up your workspace in under 60 seconds. Name it, done.' },
    { n: '02', icon: '📦', title: 'Add your products', desc: 'Import via Excel or add products manually with stock levels, groups, and MOQ.' },
    { n: '03', icon: '👥', title: 'Invite your team', desc: 'Add team members and assign roles — Admin, Manager, or Staff.' },
    { n: '04', icon: '📊', title: 'Track & analyse', desc: 'Log transactions in real time and watch your analytics update instantly.' },
  ];
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '60px' }}>
        <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6366f1', marginBottom: '10px' }}>Simple by design</p>
        <h2 style={{
          fontFamily: "'Syne',sans-serif",
          fontSize: isMobile ? '28px' : '40px',
          fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.15,
        }}>Up and running in minutes.</h2>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(4,1fr)',
        gap: '24px',
      }}>
        {steps.map(({ n, icon, title, desc }, i) => (
          <div key={n} style={{
            background: '#fff',
            border: '1.5px solid #e2e8f0',
            borderRadius: '16px',
            padding: '28px 24px',
            position: 'relative',
            transition: 'box-shadow 0.2s, transform 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(99,102,241,0.12)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
          >
            <div style={{
              position: 'absolute', top: '-12px', left: '20px',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em',
              color: '#6366f1', background: '#eef2ff',
              border: '1px solid #c7d2fe', borderRadius: '6px',
              padding: '2px 8px',
            }}>{n}</div>
            <div style={{ fontSize: '32px', marginBottom: '14px', marginTop: '6px' }}>{icon}</div>
            <h3 style={{
              fontFamily: "'Syne',sans-serif",
              fontSize: '17px', fontWeight: 700, color: '#0f172a',
              marginBottom: '8px', letterSpacing: '-0.01em',
            }}>{title}</h3>
            <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.65 }}>{desc}</p>
            {i < steps.length - 1 && !isMobile && (
              <div style={{
                position: 'absolute', right: '-14px', top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '20px', color: '#c7d2fe', zIndex: 1,
              }}>→</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


/* ══ CTA BANNER ══ */
function CTABanner({ isMobile }) {
  return (
    <div style={{
      width: '100vw', marginLeft: 'calc(-50vw + 50%)',
      background: 'linear-gradient(135deg, #0c1a3a 0%, #1e3a8a 40%, #1d4ed8 100%)',
      padding: isMobile ? '56px 20px' : '80px 0',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.05,
        backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)',
        backgroundSize: '44px 44px',
      }}/>
      <div style={{ position: 'absolute', top: '-40%', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '400px', background: 'radial-gradient(ellipse,rgba(99,102,241,0.3) 0%,transparent 70%)', pointerEvents: 'none' }}/>
      <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1, padding: '0 24px' }}>
        <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#818cf8', marginBottom: '14px' }}>Get started today</p>
        <h2 style={{
          fontFamily: "'Syne',sans-serif",
          fontSize: isMobile ? '30px' : '44px',
          fontWeight: 800, color: '#fff',
          letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: '16px',
        }}>Your inventory.<br/><span style={{ color: '#818cf8' }}>Finally under control.</span></h2>
        <p style={{ fontSize: '16px', color: '#93c5fd', lineHeight: 1.7, marginBottom: '36px', maxWidth: '480px', margin: '0 auto 36px' }}>
          Free forever. No credit card. No limits. Just your team and your stock, in perfect sync.
        </p>
        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="/signup" style={{
            display: 'inline-block', padding: '15px 36px',
            background: 'linear-gradient(135deg,#6366f1,#4f46e5)',
            color: '#fff', borderRadius: '12px', fontSize: '16px', fontWeight: 700,
            fontFamily: "'Syne',sans-serif", textDecoration: 'none',
            boxShadow: '0 4px 20px rgba(99,102,241,0.45)',
            letterSpacing: '-0.01em',
          }}>Create free account →</a>
          <a href="#login" style={{
            display: 'inline-block', padding: '15px 36px',
            background: 'rgba(255,255,255,0.08)',
            border: '1.5px solid rgba(255,255,255,0.2)',
            color: '#e2e8f0', borderRadius: '12px', fontSize: '16px', fontWeight: 600,
            textDecoration: 'none', letterSpacing: '-0.01em',
          }}>Sign in</a>
        </div>
      </div>
    </div>
  );
}

/* ══ FOOTER ══ */
function Footer({ isMobile }) {
  return (
    <div style={{
      width: '100vw', marginLeft: 'calc(-50vw + 50%)',
      background: '#0f172a', padding: isMobile ? '48px 20px 32px' : '64px 0 40px',
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '0' : '0 72px' }}>
        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', gap: isMobile ? '36px' : '0', marginBottom: '48px',
        }}>
          <div style={{ maxWidth: '280px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>📦</div>
              <span style={{ fontFamily: "'Syne',sans-serif", fontSize: '17px', fontWeight: 700, color: '#fff' }}>StockSpace</span>
            </div>
            <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.7 }}>
              Inventory management made simple. Free for teams of all sizes — no credit card required.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '28px' : '80px' }}>

            <div>
              <p style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>Product</p>
              {[
                { label: 'Features', href: '/#features' },
                { label: 'Dashboard', href: '/#dashboard' },
                { label: 'Analytics', href: '/#analytics' },
                { label: 'AI Forecast', href: '/#ai' },
                { label: 'Team Access', href: '/#team' },
                { label: 'Excel Import', href: '/#features' },
              ].map(({ label, href }) => (
                <a key={label} href={href} style={{ display: 'block', fontSize: '14px', color: '#475569', marginBottom: '10px', textDecoration: 'none', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.target.style.color = '#60a5fa'}
                  onMouseLeave={e => e.target.style.color = '#475569'}>
                  {label}
                </a>
              ))}
            </div>

            <div>
              <p style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>Legal</p>
              {[
                { label: 'Privacy Policy', href: '/privacy' },
                { label: 'Terms of Service', href: '/tos' },
              ].map(({ label, href }) => (
                <a key={label} href={href} style={{ display: 'block', fontSize: '14px', color: '#475569', marginBottom: '10px', textDecoration: 'none', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.target.style.color = '#60a5fa'}
                  onMouseLeave={e => e.target.style.color = '#475569'}>
                  {label}
                </a>
              ))}
            </div>

            <div>
              <p style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>Connect</p>
              {[
                { label: 'GitHub', href: 'https://github.com/KavyaSingla244', external: true },
                { label: 'Contact Us', href: 'mailto:stockspacesupport@gmail.com', external: true },
                { label: 'Support', href: 'mailto:stockspacesupport@gmail.com', external: true },
              ].map(({ label, href, external }) => (
                <a key={label} href={href}
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noreferrer' : undefined}
                  style={{ display: 'block', fontSize: '14px', color: '#475569', marginBottom: '10px', textDecoration: 'none', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.target.style.color = '#60a5fa'}
                  onMouseLeave={e => e.target.style.color = '#475569'}>
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div style={{
          borderTop: '1px solid #1e293b', paddingTop: '24px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px',
        }}>
          <p style={{ fontSize: '13px', color: '#334155' }}>© 2026 StockSpace. All rights reserved.</p>
          <div style={{ display: 'flex', gap: '20px' }}>
            <a href="/privacy" style={{ fontSize: '13px', color: '#334155', textDecoration: 'none' }}
              onMouseEnter={e => e.target.style.color = '#60a5fa'}
              onMouseLeave={e => e.target.style.color = '#334155'}>Privacy</a>
            <a href="/tos" style={{ fontSize: '13px', color: '#334155', textDecoration: 'none' }}
              onMouseEnter={e => e.target.style.color = '#60a5fa'}
              onMouseLeave={e => e.target.style.color = '#334155'}>Terms</a>
            <a href="mailto:stockspacesupport@gmail.com" style={{ fontSize: '13px', color: '#334155', textDecoration: 'none' }}
              onMouseEnter={e => e.target.style.color = '#60a5fa'}
              onMouseLeave={e => e.target.style.color = '#334155'}>Contact</a>
          </div>
        </div>
      </div>
    </div>
  );
}
/* ══ MAIN ══ */
export default function LandingPage() {
  const isMobile = useIsMobile();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=JetBrains+Mono:wght@400;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { -webkit-text-size-adjust: 100%; background: #f8fafc; overflow-x: hidden; }
      `}</style>

      {/* Hero */}
      <section id="login"><Login /></section>

      {/* Stats bar — immediately after hero */}
      <Reveal><StatsBar isMobile={isMobile} /></Reveal>

      <div style={{ position: 'relative', overflow: 'hidden' }}>

        {/* Feature 1 — Stock Management */}
        <Reveal>
          <FullBleed id="features" bg="linear-gradient(180deg, #f0f7ff 0%, #e8f0fe 100%)">
            <Feature
              tag="Stock Management" tagColor="#2563eb" tagBg="rgba(37,99,235,0.08)" tagBorder="rgba(37,99,235,0.2)"
              title="Every transaction, perfectly tracked."
              desc="Restock from suppliers, log sales, write-offs, returns, and adjustments — all in one unified view with colour-coded alerts."
              bullets={[
                'Five transaction types: Restock, Sale, Write-off, Return, Adjustment',
                'Instant low-stock warnings with red row highlights and badges',
                'Filter by product group — find anything in seconds',
                'Bulk Excel import & export for fast onboarding',
              ]}
              bulletColor="#2563eb"
              mockup={<StockMockup />}
              reverse={false} isMobile={isMobile}
            />
          </FullBleed>
        </Reveal>

        {/* Feature 2 — AI-Powered Intelligence (NEW) */}
        <Reveal delay={60}>
          <FullBleed id="ai" bg="linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)">
            <Feature
              tag="AI-Powered" tagColor="#6366f1" tagBg="rgba(99,102,241,0.08)" tagBorder="rgba(99,102,241,0.2)"
              title="Let Gemini watch your stock for you."
              desc="StockSpace now ships with built-in AI — a demand forecast engine that predicts stockouts before they happen, and a natural-language search that turns plain English into instant activity log queries."
              bullets={[
                'Demand Forecast predicts days-until-stockout per product, with confidence scores',
                'Smart reorder suggestions — exact quantities, not guesswork',
                'AI Search on the Activity Log: ask "who sold the most last week" in plain English',
                'Forecasts cache for 24h and refresh on demand — fast, not flaky',
              ]}
              bulletColor="#6366f1"
              mockup={<AIForecastMockup />}
              reverse={true} isMobile={isMobile}
            />
          </FullBleed>
        </Reveal>

        {/* Feature 3 — Dashboard (dedicated page, multiple highlights) */}
        <Reveal delay={60}>
          <FullBleed id="dashboard" bg="linear-gradient(180deg, #f5f3ff 0%, #ede9fe 100%)">
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
              <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8b5cf6', marginBottom: '10px' }}>Your command center</p>
              <h2 style={{
                fontFamily: "'Syne',sans-serif",
                fontSize: isMobile ? '28px' : '42px',
                fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: '14px',
              }}>One dashboard. Everything that matters.</h2>
              <p style={{ fontSize: '16px', color: '#475569', maxWidth: '600px', margin: '0 auto', lineHeight: 1.7 }}>
                The moment you log in, you see exactly where your capital sits, how sales are trending, and which products need attention — no digging required.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '56px' : '88px' }}>
              <Feature
                tag="Inventory Value" tagColor="#8b5cf6" tagBg="rgba(139,92,246,0.08)" tagBorder="rgba(139,92,246,0.2)"
                title="Your capital, broken down at a glance."
                desc="A live Inventory Value card shows exactly where your money is tied up — toggle between a by-group breakdown and a top-items view, right on the dashboard."
                bullets={[
                  'Real-time total value, recalculated as stock and prices change',
                  'Toggle between By group and Top items breakdowns',
                  'Proportional bars show each group\'s share of total value',
                  'Clearly flags how many products still need a cost price set',
                ]}
                bulletColor="#8b5cf6"
                mockup={<DashboardValueMockup />}
                reverse={false} isMobile={isMobile}
              />
            </div>
          </FullBleed>
        </Reveal>

        {/* Feature 4 — Analytics (dedicated page, both tabs) */}
        <Reveal delay={60}>
          <FullBleed id="analytics" bg="linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)">
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '64px' }}>
              <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2563eb', marginBottom: '10px' }}>Two views, one source of truth</p>
              <h2 style={{
                fontFamily: "'Syne',sans-serif",
                fontSize: isMobile ? '28px' : '42px',
                fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: '14px',
              }}>Know what's moving. Know what's worth.</h2>
              <p style={{ fontSize: '16px', color: '#475569', maxWidth: '620px', margin: '0 auto', lineHeight: 1.7 }}>
                Analytics splits cleanly into two tabs — Inventory &amp; Stock for movement and velocity, and Value &amp; Sales for revenue and ranking — so operational and financial insight never get mixed up.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '56px' : '88px' }}>
              <Feature
                tag="Inventory & Stock" tagColor="#2563eb" tagBg="rgba(37,99,235,0.08)" tagBorder="rgba(37,99,235,0.2)"
                title="Sales velocity, slow movers, and overstock — sorted."
                desc="See exactly how fast each product is moving, which items are gathering dust, and which ones you've over-ordered. Sales Velocity, Slow Movers, and Overstocked are all one click apart."
                bullets={[
                  'Stat cards: total products, units sold, write-offs, and slow movers',
                  'Sales Velocity bar chart ranks every product over your chosen period',
                  'Ranked table with units sold, average per day, and write-off counts',
                  'One-click Excel export or print for sharing with the team',
                ]}
                bulletColor="#2563eb"
                mockup={<AnalyticsStockMockup />}
                reverse={false} isMobile={isMobile}
              />
              <Feature
                tag="Value & Sales" tagColor="#16a34a" tagBg="rgba(22,163,74,0.08)" tagBorder="rgba(22,163,74,0.2)"
                title="Best sellers, dead stock, and revenue — split out."
                desc="A dedicated Value & Sales view pulls revenue insight away from stock counts entirely — best sellers, revenue by group, least sold items, and dead stock quietly tying up capital."
                bullets={[
                  'Total Revenue, Units Sold, Dead Stock SKUs, and Products Sold at a glance',
                  'Revenue by Group breakdown — once cost & sale prices are set',
                  'Best Sellers, Revenue, Least Sold, and Dead Stock sub-tabs',
                  'Same one-click Excel export and Print as Inventory Analytics',
                ]}
                bulletColor="#16a34a"
                mockup={<AnalyticsValueMockup />}
                reverse={true} isMobile={isMobile}
              />
            </div>
          </FullBleed>
        </Reveal>

        {/* Feature 5 — Activity Log + AI Search */}
        <Reveal delay={60}>
          <FullBleed bg="linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)">
            <Feature
              tag="Activity Log" tagColor="#d97706" tagBg="rgba(217,119,6,0.08)" tagBorder="rgba(217,119,6,0.2)"
              title="Every action, searchable in plain English."
              desc="Every restock, sale, write-off, return, and adjustment is logged automatically. Filter by period, type, or user — or just ask the AI Search bar what you want to know."
              bullets={[
                'AI Search (powered by Gemini) — ask "write-offs by Rahul" and get pre-filled results',
                'Filter by period (24h, 7d, 30d, year, all-time), transaction type, or user',
                'Full audit trail: who did what, and when',
                'Export the entire log to Excel for record-keeping',
              ]}
              bulletColor="#d97706"
              mockup={<ActivityLogMockup />}
              reverse={false} isMobile={isMobile}
            />
          </FullBleed>
        </Reveal>

        {/* Feature 6 — Team Access */}
        <Reveal delay={60}>
          <FullBleed id="team" bg="linear-gradient(180deg, #f0fdf4 0%, #dcfce7 100%)">
            <Feature
              tag="Team Access" tagColor="#059669" tagBg="rgba(5,150,105,0.08)" tagBorder="rgba(5,150,105,0.2)"
              title="The right people see the right things."
              desc="Admin, Manager, and Staff roles built in. Admins control everything. Managers update stock. Staff see what they need — nothing more."
              bullets={[
                'Three roles: Admin, Manager, Staff — set it and forget it',
                'Admins manage users, products, and organisation settings',
                'Managers can update stock and log all transaction types',
                'All roles see live data — zero caching, zero stale views',
              ]}
              bulletColor="#059669"
              mockup={<TeamMockup />}
              reverse={false} isMobile={isMobile}
            />
          </FullBleed>
        </Reveal>

        {/* How it works — white */}
        <Reveal delay={40}>
          <FullBleed bg="#ffffff">
            <HowItWorks isMobile={isMobile} />
          </FullBleed>
        </Reveal>

        {/* CTA banner */}
        <Reveal delay={40}>
          <CTABanner isMobile={isMobile} />
        </Reveal>

        {/* Footer */}
        <Footer isMobile={isMobile} />

      </div>
    </>
  );
}